import React, { useState, useEffect } from 'react';
import { Search, Plus, Filter, Star } from 'lucide-react';
import { searchConditions, conditionCategories } from '../../data/medicalConditions';
import { MedicalCondition } from '../../types/medical';

interface ConditionSearchProps {
  onConditionSelect: (condition: MedicalCondition) => void;
  selectedConditions: string[];
}

export const ConditionSearch: React.FC<ConditionSearchProps> = ({ 
  onConditionSelect, 
  selectedConditions 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchResults, setSearchResults] = useState<MedicalCondition[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    let results = searchConditions(searchQuery);
    
    if (selectedCategory !== 'all') {
      results = results.filter(condition => condition.category === selectedCategory);
    }

    // Sort by prevalence and alphabetically
    results.sort((a, b) => {
      const prevalenceOrder = { 'very-common': 0, 'common': 1, 'uncommon': 2, 'rare': 3 };
      const aPrev = prevalenceOrder[a.prevalence];
      const bPrev = prevalenceOrder[b.prevalence];
      
      if (aPrev !== bPrev) return aPrev - bPrev;
      return a.name.localeCompare(b.name);
    });

    setSearchResults(results);
  }, [searchQuery, selectedCategory]);

  const getPrevalenceColor = (prevalence: MedicalCondition['prevalence']) => {
    switch (prevalence) {
      case 'very-common': return 'bg-green-100 text-green-800';
      case 'common': return 'bg-blue-100 text-blue-800';
      case 'uncommon': return 'bg-yellow-100 text-yellow-800';
      case 'rare': return 'bg-red-100 text-red-800';
    }
  };

  const commonConditions = searchConditions('').filter(c => c.prevalence === 'very-common').slice(0, 6);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Add Medical Condition</h3>
        
        {/* Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search conditions (e.g., diabetes, hypertension, depression...)"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Category Filter */}
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {conditionCategories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        {/* Quick Suggestions - Common Conditions */}
        {!searchQuery && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center">
              <Star className="h-4 w-4 mr-1 text-yellow-500" />
              Most Common Conditions
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {commonConditions.map(condition => (
                <button
                  key={condition.id}
                  onClick={() => onConditionSelect(condition)}
                  disabled={selectedConditions.includes(condition.id)}
                  className="text-left p-3 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-800">{condition.name}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getPrevalenceColor(condition.prevalence)}`}>
                      {condition.prevalence.replace('-', ' ')}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">{condition.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {showSuggestions && searchQuery && (
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-lg">
            {searchResults.length > 0 ? (
              <div className="divide-y divide-slate-200">
                {searchResults.slice(0, 20).map(condition => (
                  <button
                    key={condition.id}
                    onClick={() => {
                      onConditionSelect(condition);
                      setShowSuggestions(false);
                      setSearchQuery('');
                    }}
                    disabled={selectedConditions.includes(condition.id)}
                    className="w-full text-left p-4 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-800">{condition.name}</span>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${getPrevalenceColor(condition.prevalence)}`}>
                          {condition.prevalence.replace('-', ' ')}
                        </span>
                        <span className="text-xs text-slate-500">{condition.category}</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">{condition.description}</p>
                    {condition.icd10Code && (
                      <p className="text-xs text-slate-500 mt-1">ICD-10: {condition.icd10Code}</p>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-slate-500">
                <p>No conditions found matching "{searchQuery}"</p>
                <button className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium">
                  Request to add this condition
                </button>
              </div>
            )}
          </div>
        )}

        {/* Custom Condition */}
        <div className="mt-4 pt-4 border-t border-slate-200">
          <button className="w-full flex items-center justify-center space-x-2 p-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:text-slate-800 hover:border-slate-400 transition-colors">
            <Plus className="h-4 w-4" />
            <span>Add custom condition not listed</span>
          </button>
        </div>
      </div>
    </div>
  );
};
